import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  ModalFooter,
  Button,
  Checkbox,
  Stack,
  Flex,
  FormLabel,
  Input,
  useToast,
  Image
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { UserService } from '../../../services/User/UserService'
import 'react-image-crop/dist/ReactCrop.css'

interface IProps {
  isOpen: boolean
  onClose: () => void
  roles: { id: number; name: string }[]
  user: any
}

interface IForm {
  name: string
  email: string
  password: string
  password_confirmation: string
  roles: number[]
}

export function UserEditModal({ isOpen, onClose, roles, user }: IProps) {
  const userService = new UserService()
  const [form, setForm] = useState<IForm>({} as IForm)
  const [image, setImage] = useState<File>()
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})
  const [changePassword, setChangePassword] = useState(false)
  const toast = useToast()

  useEffect(() => {
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      password_confirmation: '',
      roles: user.roles.map((role: string) => roles.find((r) => r.name === role)?.id)
    })
  }, [user])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formValid()) {
      return
    }

    userService
      .update(user.id, { ...form, picture: image }, changePassword)
      .then(() => {
        toast({
          title: 'Usuário criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true
        })

        onClose()
      })
      .catch((error) => {
        setErrors(error.response.data.errors)
      })
  }

  const formValid = () => {
    return (
      form.name &&
      form.email &&
      (!changePassword || (form.password && form.password === form.password_confirmation)) &&
      form.roles.length > 0
    )
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <form onSubmit={handleSave}>
        <ModalContent>
          <ModalHeader>Alterar usuário {user.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column">
              <FormLabel>Nome</FormLabel>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              {errors.name &&
                errors.name.map((error, index) => (
                  <Text key={index} fontSize="sm" color="red.500">
                    {error}
                  </Text>
                ))}
              <FormLabel mt={4}>Email</FormLabel>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              {errors.email &&
                errors.email.map((error, index) => (
                  <Text key={index} fontSize="sm" color="red.500">
                    {error}
                  </Text>
                ))}

              <Flex direction="row" align="center" mt={4}>
                <Checkbox
                  isChecked={changePassword}
                  onChange={(e) => setChangePassword(e.target.checked)}
                  mr={2}
                  id="change-password"
                />

                <FormLabel mt={2} htmlFor="change-password">
                  Alterar senha
                </FormLabel>
              </Flex>

              {changePassword && (
                <>
                  <FormLabel mt={4}>Senha</FormLabel>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    autoComplete="new-password"
                  />
                  {errors.password &&
                    errors.password.map((error, index) => (
                      <Text key={index} fontSize="sm" color="red.500">
                        {error}
                      </Text>
                    ))}

                  <FormLabel mt={4}>Confirmação de senha</FormLabel>
                  <Input
                    type="password"
                    value={form.password_confirmation}
                    onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                    mb={form.password !== form.password_confirmation ? 0 : 4}
                    required
                  />

                  {form.password !== form.password_confirmation && (
                    <Text fontSize="sm" color="red.500" mb={4}>
                      As senhas não coincidem
                    </Text>
                  )}
                </>
              )}

              <FormLabel mt={4}>Foto de perfil</FormLabel>
              <Flex align="center">
                {image && (
                  <Image src={URL.createObjectURL(image)} alt={form.name} width="40px" height="40px" marginEnd={2} />
                )}
                <Input type="file" p={1} onChange={(e) => setImage(e.target?.files?.[0])} accept=".jpg,.jpeg,.png" />
              </Flex>
              {errors.picture &&
                errors.picture.map((error, index) => (
                  <Text key={index} fontSize="sm" color="red.500">
                    {error}
                  </Text>
                ))}
            </Flex>
            <Stack direction="column">
              <FormLabel mt={4}>Grupos de usuário</FormLabel>
              {roles.map((role) => (
                <Checkbox
                  key={role.id}
                  isChecked={form.roles.includes(role.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setForm({ ...form, roles: [...form.roles, role.id] })
                    } else {
                      setForm({ ...form, roles: form.roles.filter((id) => id !== role.id) })
                    }
                  }}
                >
                  {role.name}
                </Checkbox>
              ))}
            </Stack>
            {!formValid() && (
              <Text mt={4} fontSize="sm" color="gray.500">
                Preencha todos os campos acima para prosseguir
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSave} type="submit" disabled={!formValid()}>
              Salvar
            </Button>
            <Button variant="ghost" onClick={onClose} type="button">
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </ChakraModal>
  )
}
